<template>
  <div class="audio-element-wrapper">
    <div class="element-header">
      <span class="element-type-badge">
        <i class="fas fa-volume-up"></i>
        <span>Audio</span>
      </span>
      <div class="element-actions">
        <button class="action-btn" @click.stop="$emit('duplicate')" title="Dupliquer">
          <i class="fas fa-copy"></i>
        </button>
        <button class="action-btn delete-btn" @click.stop="$emit('remove')" title="Supprimer">
          <i class="fas fa-trash"></i>
        </button>
      </div>
    </div>
    
    <div class="audio-element">
      <div v-if="element.link || localAudioUrl" class="audio-container">
        <audio controls class="audio-player" :key="audioKey">
          <source :src="localAudioUrl || element.link" type="audio/mpeg">
          <source :src="localAudioUrl || element.link" type="audio/wav">
          <source :src="localAudioUrl || element.link" type="audio/mp3">
          Votre navigateur ne supporte pas l'élément audio.
        </audio>
        <div class="audio-info">
          <span class="audio-filename">{{ getFileName(localAudioUrl || element.link) }}</span>
          <button class="change-audio-btn" @click="uploadAudio" title="Changer l'audio" :disabled="isUploading">
            <svg v-if="!isUploading" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
              <line x1="12" y1="19" x2="12" y2="23"></line>
              <line x1="8" y1="23" x2="16" y2="23"></line>
            </svg>
            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="uploading-spinner">
              <circle cx="12" cy="12" r="10" opacity="0.3"></circle>
              <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"></path>
            </svg>
            <span>{{ isUploading ? 'Upload...' : 'Changer' }}</span>
          </button>
        </div>
      </div>
      <div v-else class="audio-placeholder" :class="{ 'uploading': isUploading }">
        <svg v-if="!isUploading" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
          <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
          <line x1="12" y1="19" x2="12" y2="23"></line>
          <line x1="8" y1="23" x2="16" y2="23"></line>
        </svg>
        <svg v-else width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="uploading-spinner">
          <circle cx="12" cy="12" r="10" opacity="0.3"></circle>
          <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"></path>
        </svg>
        <p v-if="!isUploading">Aucun fichier audio sélectionné</p>
        <p v-else>Upload en cours...</p>
        <button class="upload-audio-btn" @click="uploadAudio" :disabled="isUploading">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7,10 12,15 17,10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          <span>Sélectionner un fichier audio</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { axiosPeelo } from '@/utils/axios'
import { apiBaseUrl } from '@/config'

export default {
  name: 'AudioElement',
  props: {
    element: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      isUploading: false,
      localAudioUrl: null, // URL locale pour afficher l'audio sélectionné
      audioKey: Date.now() // Clé pour forcer le rechargement du lecteur audio
    }
  },
  methods: {
    // Méthode pour déclencher l'upload d'audio
    uploadAudio() {
       // Empêcher les clics multiples pendant l'upload
      if (this.isUploading) {
         return;
      }
      
      // Créer un élément input file dynamique (plus fiable)
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = '.mp3,.wav,.ogg,audio/mpeg,audio/wav,audio/ogg';
      fileInput.style.display = 'none';
      
      // Ajouter l'événement de changement
      fileInput.addEventListener('change', (event) => {
         this.handleAudioUpload(event);
      });
      
      // Ajouter au DOM, déclencher le clic, puis supprimer
      document.body.appendChild(fileInput);
      fileInput.click();
      setTimeout(() => {
        if (document.body.contains(fileInput)) {
          document.body.removeChild(fileInput);
        }
      }, 1000);
    },
    
    // Méthode pour gérer l'upload d'audio
    async handleAudioUpload(event) {
      // Créer un ID unique pour cet upload
      const uploadId = Date.now() + '-' + Math.random().toString(36).substr(2, 9);
       const file = event.target.files[0];
       if (!file) {
         return;
      }
      
      // Vider complètement le cache audio avant de commencer
      this.clearAudioCache();
       // Nettoyer l'ancienne URL locale si elle existe
      if (this.localAudioUrl) {
         URL.revokeObjectURL(this.localAudioUrl);
        this.localAudioUrl = null;
      }
      
      // Forcer le nettoyage complet de l'ancien audio
      if (this.element.link) {
         // Émettre temporairement un élément sans link pour forcer le rechargement
        this.$emit('update', { ...this.element, link: null });
      }
      
       // Vérifier que c'est bien un fichier audio avec les formats autorisés
      const fileName = file.name.toLowerCase();
      const allowedTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg'];
      const allowedExtensions = ['.mp3', '.wav', '.ogg'];
      
      const isValidType = allowedTypes.includes(file.type);
      const isValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
      
      if (!isValidType || !isValidExtension) {
        this.$emit('notification', {
          message: "Format non autorisé. Seuls les fichiers MP3, WAV et OGG sont acceptés.",
          type: "error"
        });
        return;
      }
      
      // Vérifier la taille du fichier (10 Mo maximum pour l'audio)
      const maxSizeInBytes = 10 * 1024 * 1024; // 10 Mo en bytes
      if (file.size > maxSizeInBytes) {
        const fileSizeInMB = (file.size / (1024 * 1024)).toFixed(2);
        this.$emit('notification', {
          message: `Le fichier audio est trop volumineux (${fileSizeInMB} Mo). Taille maximum autorisée : 10 Mo`,
          type: "error"
        });
        return;
      }
      
      // Démarrer l'état de chargement
      this.isUploading = true;
      
      // Attendre un peu pour que le nettoyage soit effectif
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Créer une URL locale pour afficher l'audio immédiatement
      this.localAudioUrl = URL.createObjectURL(file);
       try {
        // Créer FormData pour l'upload (nouveau pour chaque upload)
        const formData = new FormData();
        formData.append('audiofromplayground', file);
        
         // Récupérer le token d'authentification
        const userData = localStorage.getItem('user');
        if (!userData) {
          throw new Error('Session expirée, veuillez vous reconnecter');
        }
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
         // Configuration des headers optimisée
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 60000, // 60 secondes timeout
          maxContentLength: Infinity,
          maxBodyLength: Infinity
        };
        
           // Horodatage avant l'envoi
        const startTime = Date.now();
         // Envoyer l'audio au serveur
        const response = await axiosPeelo.post(
          '/strong/upload/audio/block',
          formData,
          config
        );
        
        const endTime = Date.now();
        const uploadDuration = ((endTime - startTime) / 1000).toFixed(2);
         if (response.data && response.data.success) {
          // Construire l'URL complète de l'audio avec apiBaseUrl
          // Nettoyer le filePath pour éviter les doubles slashes et enlever 'public/'
          let cleanFilePath = response.data.filePath;
          
          // Enlever le préfixe 'public/' car les fichiers publics sont servis depuis la racine
         
          
         
          
          const audioUrl = `${apiBaseUrl}/${cleanFilePath}`;
          
           // Mettre à jour l'élément avec le lien de l'audio du serveur
          const updatedElement = { ...this.element, link: audioUrl };
          
          // Émettre l'événement de mise à jour
          this.$emit('update', updatedElement);
          
          // Émettre une notification de succès
          this.$emit('notification', {
            message: "Audio uploadé avec succès",
            type: "success"
          });
          
           } else {
          throw new Error(response.data?.message || 'Erreur lors de l\'upload');
        }
        
      } catch (error) {
        console.error(`❌ [${uploadId}] Erreur lors de l'upload de l'audio:`, error);
        
        let errorMessage = 'Erreur lors de l\'upload de l\'audio';
        
        if (error.response) {
          // Erreur de réponse du serveur
          errorMessage = error.response.data?.message || 
                        error.response.data?.error || 
                        `Erreur serveur (${error.response.status})`;
        } else if (error.request) {
          // Erreur de réseau
          errorMessage = 'Impossible de contacter le serveur';
        } else {
          // Autre erreur
          errorMessage = error.message || errorMessage;
        }
        
        // Émettre une notification d'erreur
        this.$emit('notification', {
          message: errorMessage,
          type: "error"
        });
        
      } finally {
        // Arrêter l'état de chargement
        this.isUploading = false;
        
         }
    },
    
    getFileName(url) {
      if (!url) return 'Fichier audio';
      const parts = url.split('/');
      return parts[parts.length - 1];
    },
    
    // Méthode pour vider le cache audio complètement
    clearAudioCache() {
       // Changer la clé pour forcer le rechargement du composant audio
      this.audioKey = Date.now();
      
      // Si il y a une URL locale, la nettoyer
      if (this.localAudioUrl) {
        URL.revokeObjectURL(this.localAudioUrl);
        this.localAudioUrl = null;
      }
      
      // Forcer le garbage collection si possible
      if (window.gc) {
        window.gc();
      }
    }
  },
  
  // Nettoyer l'URL locale quand le composant est détruit
  beforeUnmount() {
    if (this.localAudioUrl) {
      URL.revokeObjectURL(this.localAudioUrl);
    }
  }
}
</script>

<style scoped>
.audio-element-wrapper {
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.95), rgba(249, 250, 251, 0.9));
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: all 0.2s ease;
  margin-bottom: 20px;
}

.audio-element-wrapper:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.element-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(145deg, rgba(249, 250, 251, 0.8), rgba(243, 244, 246, 0.6));
  border-bottom: 1px solid rgba(229, 231, 235, 0.5);
}

.element-type-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #0284c7;
  font-size: 0.875rem;
}

.element-type-badge i {
  color: #0284c7;
}

.element-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(249, 250, 251, 0.8));
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.action-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.action-btn.delete-btn:hover {
  background: linear-gradient(145deg, rgba(239, 68, 68, 0.15), rgba(239, 68, 68, 0.05));
  color: #ef4444;
}

.audio-element {
  padding: 16px;
}

.audio-placeholder {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  color: #666;
  border: 2px dashed #dee2e6;
  font-weight: 600;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  transition: all 0.2s ease;
}

.audio-placeholder:hover {
  background: linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%);
  border-color: #adb5bd;
}

.audio-placeholder p {
  margin: 0;
  font-size: 0.9rem;
}

.audio-container {
  position: relative;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.audio-player {
  width: 100%;
  border-radius: 12px;
  background: #f8f9fa;
}

.audio-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.audio-filename {
  font-size: 0.875rem;
  color: #0284c7;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 60%;
}

.change-audio-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(2, 132, 199, 0.2);
}

.change-audio-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #0369a1 0%, #075985 100%);
  box-shadow: 0 4px 8px rgba(2, 132, 199, 0.3);
  transform: translateY(-1px);
}

.change-audio-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.upload-audio-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 8px rgba(2, 132, 199, 0.2);
  margin-top: 8px;
}

.upload-audio-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #0369a1 0%, #075985 100%);
  box-shadow: 0 6px 12px rgba(2, 132, 199, 0.3);
  transform: translateY(-2px);
}

.upload-audio-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.uploading-spinner {
  animation: spin 1s linear infinite;
}

.audio-placeholder.uploading {
  background: linear-gradient(135deg, #f0f9ff 0%, #dbeafe 100%);
  border-color: #3b82f6;
  color: #2563eb;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
